import { Bug } from 'lucide-react';
import { useState } from 'react';

import { useMe } from '@/api/hooks.js';
import { useHandshakeContext } from '@/components/HandshakeContext.js';

/**
 * Tenant, environment, principal and handshake details, tucked behind a toggle
 * because they are support information rather than something a user acts on.
 *
 * They stay one click away because "which tenant is this actually talking to"
 * is the first question worth asking whenever the extension misbehaves, and
 * inside an iframe there is nothing else on screen that answers it.
 */
export function DebugPanel() {
  const [open, setOpen] = useState(false);
  const me = useMe();
  const handshake = useHandshakeContext();
  const details = handshake.extensionDetails;

  const rows: [string, string][] = [
    ['Tenant', me.data ? me.data.tenant.label || me.data.tenant.tenantDomain : '—'],
    ['Domain', me.data?.tenant.tenantDomain || '—'],
    ['Tenant id', me.data?.tenant.id || '—'],
    ['Theme', me.data?.tenant.theme || '—'],
    ['Environment', me.data?.environment || '—'],
    ['User id', me.data?.userId || '—'],
    ['Scopes', me.data?.scopes.join(', ') || '—'],
    ['Extension id', details?.extensionId || '—'],
    ['Space', details?.spaceId || '—'],
    ['Page', details?.pageId || '—'],
    ['Locale', details?.locale || '—'],
    ['Host origin', handshake.hostOrigin || '—'],
    ['Token expires', formatExpiry(handshake.expires)],
  ];

  return (
    <div className="fixed bottom-4 left-4 z-50">
      {open ? (
        <dl className="mb-2 max-h-[70vh] w-80 overflow-auto rounded-brand border border-line bg-surface p-3 text-xs shadow-lg">
          {rows.map(([label, value]) => (
            <div
              key={label}
              className="flex gap-3 border-b border-line-subtle py-1.5 last:border-0"
            >
              <dt className="w-28 shrink-0 text-muted">{label}</dt>
              <dd className="min-w-0 break-all text-body">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      <button
        type="button"
        aria-label="Debug information"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-line bg-surface text-muted transition hover:text-ink"
      >
        <Bug className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Expiry is the one field worth reading as a clock time rather than a claim. */
function formatExpiry(expires: string | null): string {
  if (!expires) return '—';
  const date = new Date(Number(expires) * 1000);
  return Number.isNaN(date.getTime()) ? expires : date.toLocaleTimeString();
}
